/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useCallback, useMemo } from 'react';
import { FlatList, View, RefreshControl, ViewToken } from 'react-native';
import { useDeviceInfo } from '@airasia-rn/ui';
import styles from './MasonryGrid.styles';

type RenderItem<T> = (item: T, index: number) => React.ReactElement | null;

interface MasonryGridProps<T> {
  data: T[];
  renderItem: RenderItem<T>;
  ListFooterComponent?: React.ReactElement | null;
  keyExtractor?: (item: T, index: number) => string;
  onEndReached?: () => void;
  refreshing?: boolean;
  onRefresh?: () => void;
  refreshControl?: React.ReactElement;
  onViewableItemsChanged?: (params: {
    viewableItems: Array<ViewToken>;
    changed: Array<ViewToken>;
  }) => void;
}

type PlaceholderItem = { __placeholder: true; __placeholderKey: string };

const MasonryGrid = <T,>({
  data,
  renderItem,
  ListFooterComponent,
  keyExtractor,
  onEndReached,
  refreshing,
  onRefresh,
  onViewableItemsChanged,
}: MasonryGridProps<T>) => {
  const { isMobileWidth } = useDeviceInfo();
  const numColumns = useMemo(() => {
    return isMobileWidth ? 2 : 4;
  }, [isMobileWidth]);

  const getItemKey = useCallback(
    (item: T | PlaceholderItem, index: number) => {
      if ((item as PlaceholderItem).__placeholder) {
        return (item as PlaceholderItem).__placeholderKey;
      }
      return (
        keyExtractor?.(item as T, index) ??
        (item as any)?.lobItemId ??
        `${index}`
      );
    },
    [keyExtractor],
  );

  const handleViewableItemsChanged = useCallback(
    (params: {
      viewableItems: Array<ViewToken>;
      changed: Array<ViewToken>;
    }) => {
      onViewableItemsChanged?.(params);
    },
    [onViewableItemsChanged],
  );

  if (isMobileWidth) {
    const columns = Array.from(
      { length: numColumns },
      () => [] as { item: T; idx: number }[],
    );
    data.forEach((item, i) => columns[i % numColumns].push({ item, idx: i }));

    return (
      <View style={styles.contentContainerMobile}>
        <View style={styles.mobileGrid}>
          {columns.map((col, colIndex) => (
            <View key={colIndex} style={styles.mobileColumn}>
              {col.map(({ item, idx }) => (
                <View
                  key={
                    keyExtractor?.(item, idx) ??
                    (item as any)?.lobItemId ??
                    String(idx)
                  }
                  style={styles.mobileItem}
                >
                  {renderItem(item, idx)}
                </View>
              ))}
            </View>
          ))}
        </View>
        {ListFooterComponent}
      </View>
    );
  }

  const dataWithPlaceholders = (() => {
    const items = [...data] as Array<T | PlaceholderItem>;
    const remainder = items.length % numColumns;
    if (remainder === 0) return items;
    const needed = numColumns - remainder;
    for (let i = 0; i < needed; i += 1) {
      items.push({
        __placeholder: true,
        __placeholderKey: `placeholder-${items.length}-${i}`,
      });
    }
    return items;
  })();

  return (
    <FlatList
      key={`masonry-grid-${numColumns}`}
      refreshControl={
        <RefreshControl
          refreshing={refreshing || false}
          onRefresh={onRefresh}
        />
      }
      scrollEnabled={false}
      data={dataWithPlaceholders}
      numColumns={numColumns}
      keyExtractor={getItemKey}
      columnWrapperStyle={styles.columnWrapper}
      renderItem={({ item, index }) => (
        <View style={styles.itemContainer}>
          {(item as PlaceholderItem).__placeholder
            ? null
            : renderItem(item as T, index)}
        </View>
      )}
      ListFooterComponent={ListFooterComponent}
      onEndReached={onEndReached}
      onEndReachedThreshold={0.3}
      onViewableItemsChanged={handleViewableItemsChanged}
      viewabilityConfig={{ itemVisiblePercentThreshold: 50 }}
      showsVerticalScrollIndicator={false}
      contentContainerStyle={styles.contentContainerDesktop}
    />
  );
};

export default MasonryGrid;
